import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectTrainingRunPersons,
  selectTrainingRunSelectedLang,
  selectTrainingRunVersions,
  trainingRunActions,
} from '../../store/entities/trainingRun';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { TrainingRunSign } from './TrainingRunSign';
import { TrainingRunQuestion, TrainingRunQuestionnaire } from './TrainingRunQuestionnaire';
import { TrainingRunOverview } from './TrainingRunOverview';
import { SlideDto } from '@api/models/training/SlideDto';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import {
  companyService,
  questionnaireService,
  questionnaireVersionService,
  supplierEmployeeService,
  trainingVersionService,
} from '@api/services/services';
import { selectIdentity } from '../../store/entities/identity';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { QcsBox } from '../common/basic/QcsBox';
import { HtmlViewer } from '../common/html/HtmlViewer';
import { TourniquetButtonContent } from '../tourniquet/TourniquetButtonContent';
import { TourniquetSlideCard } from '../tourniquet/TourniquetStyles';
import { getFirstLastNameObj } from '../../utils/format';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { apiMessagesStore } from '@api/utils/ApiMessagesStore';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { LanguageDto } from '@api/models/settings/LanguageDto';
import { AnswerResponseType } from '@api/models/questionnaire/AnswerResponseType';
import { PageType } from '@api/models/enums/PageType';

type TrainingRunMode = 'training' | 'trainingSign' | 'questionnaire' | 'overview';

// Resources that gate the run view via ShowResourceContent. Cleared before each (re)load so a
// previous failure (e.g. a language with no content) doesn't keep the error banner up after a
// successful reload — makeRequest only sets errors, it never clears them on success.
const TRAINING_RUN_RESOURCES = [
  resources.getTrainingVersion,
  resources.getSlides,
  resources.getCurrentQuestionnaireVersion,
  resources.getQuestionnaireVersion,
  resources.getQuestions,
];

// Finds an available language matching a frontend code (from i18n or company settings).
// Matches via flag because API codes may differ from SUPPORTED_LANGUAGES codes (e.g., API "ger" vs frontend "de").
// Falls back to case-insensitive code match for safety.
const findByFrontendCode = (availableLanguages: LanguageDto[], frontendCode: string): LanguageDto | undefined => {
  const supported = SUPPORTED_LANGUAGES.find((s) => s.code === frontendCode);
  if (supported) {
    const match = availableLanguages.find((l) => l.flag.toLowerCase() === supported.flag.toLowerCase());
    if (match) return match;
  }
  return availableLanguages.find((l) => l.code.toLowerCase() === frontendCode.toLowerCase());
};

const resolveDefaultLang = (availableLanguages: LanguageDto[], langParam: string | null, companyDefaultLang: string | undefined): string => {
  if (langParam) {
    const match = findByFrontendCode(availableLanguages, langParam);
    if (match) return match.code;
  }
  if (companyDefaultLang) {
    const match = findByFrontendCode(availableLanguages, companyDefaultLang);
    if (match) return match.code;
  }
  return availableLanguages[0].code;
};

export const TrainingRunWrapper: FC = () => {
  const { versionId } = useParams<{ versionId: string }>();
  const [searchParams] = useSearchParams();
  const isGroup = searchParams.get('group') === 'true';
  const supplierEmployeeId = searchParams.get('supplierEmployeeId');
  const isPlayground = searchParams.get('playground') === 'true';
  const isQuestionnaireMode = searchParams.get('questionnaireMode') === 'true';
  const langParam = searchParams.get('lang');
  const { goBack, navigate } = useNavigation();
  const dispatch = useAppDispatch();

  const identity = useAppSelector(selectIdentity);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const personsFromRedux = useAppSelector(selectTrainingRunPersons);
  const versionsFromRedux = useAppSelector(selectTrainingRunVersions);

  const [mode, setMode] = useState<TrainingRunMode>('training');
  const [slideIndex, setSlideIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);

  const [slides, setSlides] = useState<SlideDto[]>([]);
  const [questions, _setQuestions] = useState<TrainingRunQuestion[]>([]);
  const questionsRef = useRef(questions);
  const setQuestions = useCallback((update: TrainingRunQuestion[] | ((prev: TrainingRunQuestion[]) => TrainingRunQuestion[])) => {
    _setQuestions((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      questionsRef.current = next;
      return next;
    });
  }, []);
  const [successRate, setSuccessRate] = useState<number>();
  const [afterTrainingContent, setAfterTrainingContent] = useState<string>();
  const [questionnaireId, setQuestionnaireId] = useState<string>();
  const [singlePerson, setSinglePerson] = useState<ReferenceDto>();
  const [questionnaireResult, setQuestionnaireResult] = useState<{ score: number; requiredScore: number }>();

  const lang = useAppSelector(selectTrainingRunSelectedLang);

  const persons: ReferenceDto[] = isGroup ? personsFromRedux : singlePerson ? [singlePerson] : [];
  const initialLoadRef = useRef(true);
  // In playground mode the questionnaire version is pinned to the run version's validFrom date so the
  // played questionnaire matches the training version being previewed, not just today's current version.
  const playgroundDateRef = useRef<string | undefined>(undefined);

  const loadAfterTrainingContent = async (langCode: string): Promise<string | undefined> => {
    // if there is no version for the selected language, fall back to english, then to the company default language
    const languages = [langCode, 'en', companyCustomization?.defaultLanguage].filter(
      (lang, index, arr): lang is string => !!lang && arr.indexOf(lang) === index,
    );
    for (const language of languages) {
      const res = await companyService.queryCompanyPages({ type: PageType.AfterTraining, language });
      const content = res.data[0]?.content;
      if (content) return content;
    }
    return undefined;
  };

  const loadContent = async (langCode: string, qId?: string) => {
    const [fetchedSlides, afterContent, fetchedQuestionnaire] = await Promise.all([
      trainingVersionService.getSlides(versionId!, langCode, { throwErrorIfEmpty: true }),
      loadAfterTrainingContent(langCode),
      qId ? questionnaireService.getCurrentQuestionnaireVersion(qId, langCode, undefined, playgroundDateRef.current) : Promise.resolve(undefined),
    ]);

    setSlides(fetchedSlides.data);
    setAfterTrainingContent(afterContent);

    if (qId && fetchedQuestionnaire) {
      setSuccessRate(fetchedQuestionnaire.data.successRate);
      setQuestions(
        fetchedQuestionnaire.data.questions.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map((q) => ({ ...q, answerResponse: undefined })),
      );
    }

    setSlideIndex(0);
    setQuestionIndex(0);

    return fetchedSlides.data.length === 0;
  };

  const loadQuestionnaireContent = async (langCode: string) => {
    const fetchedQuestions = await questionnaireVersionService.getQuestions(versionId!, langCode);
    setQuestions(fetchedQuestions.data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map((q) => ({ ...q, answerResponse: undefined })));
    setQuestionIndex(0);
  };

  // Load training data
  useEffect(() => {
    if (!versionId) return;

    initialLoadRef.current = true;

    const loadData = async () => {
      TRAINING_RUN_RESOURCES.forEach((res) => apiMessagesStore.clearError(res));
      setQuestionnaireResult(undefined);
      setQuestions([]);
      setQuestionnaireId(undefined);
      setQuestionIndex(0);

      if (isQuestionnaireMode) {
        const versionRes = await questionnaireVersionService.getById(versionId, { throwErrorIfEmpty: true });
        const version = versionRes.data;

        const availableLanguages = version.languages ?? [];
        dispatch(trainingRunActions.setLanguages(availableLanguages));

        const defaultLang = resolveDefaultLang(availableLanguages, langParam, companyCustomization?.defaultLanguage);
        dispatch(trainingRunActions.setSelectedLang(defaultLang));

        setSuccessRate(version.successRate);
        await loadQuestionnaireContent(defaultLang);
        setMode('questionnaire');
      } else {
        setMode('training');

        if (supplierEmployeeId && !isGroup) {
          // The launcher (TrainingRunButton) already knows the name and puts it in the store, so no
          // request is needed in the normal flow. On a page reload / deep link the store is empty
          // (it is not persisted) and only the id from the url survives - fetch the name then.
          const personFromStore = personsFromRedux.find((p) => p.id === supplierEmployeeId);
          if (personFromStore) {
            setSinglePerson(personFromStore);
          } else {
            const empRes = await supplierEmployeeService.getById(supplierEmployeeId);
            setSinglePerson({ id: supplierEmployeeId, name: getFirstLastNameObj(empRes.data) });
          }
        } else if (isPlayground && !isGroup) {
          setSinglePerson({ id: identity.id, name: identity.name ?? '' });
        }

        const versionAxiosR = await trainingVersionService.getById(versionId, { throwErrorIfEmpty: true });
        const version = versionAxiosR.data;

        playgroundDateRef.current = isPlayground ? version.validFrom : undefined;

        const availableLanguages = version.languageVariants!.map((v) => v.language) ?? [];
        dispatch(trainingRunActions.setLanguages(availableLanguages));

        const defaultLang = resolveDefaultLang(availableLanguages, langParam, companyCustomization?.defaultLanguage);
        dispatch(trainingRunActions.setSelectedLang(defaultLang));

        const qId = version.questionnaire?.id;
        if (qId) setQuestionnaireId(qId);

        const noSlides = await loadContent(defaultLang, qId);
        if (noSlides) setMode('trainingSign');
      }
    };

    loadData();
  }, [versionId]);

  // Reload content when language changes (triggered from Header)
  useEffect(() => {
    if (!lang || !versionId) return;
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    const reloadForLang = async () => {
      TRAINING_RUN_RESOURCES.forEach((res) => apiMessagesStore.clearError(res));
      if (isQuestionnaireMode) {
        await loadQuestionnaireContent(lang);
        setMode('questionnaire');
      } else {
        const noSlides = await loadContent(lang, questionnaireId);
        if (noSlides) setMode('trainingSign');
      }
    };

    reloadForLang();
  }, [lang]);

  const handleAbort = useCallback(() => {
    goBack('/');
  }, [goBack]);

  // Slide navigation
  const handleSlidePrev = useCallback(() => {
    setSlideIndex((prev) => prev - 1);
  }, []);

  const handleSlideNext = useCallback(() => {
    if (slideIndex === slides.length - 1) {
      if (questionsRef.current.length > 0) {
        setMode('questionnaire');
        setQuestionIndex(0);
      } else {
        setMode('trainingSign');
      }
      return;
    }
    setSlideIndex((prev) => prev + 1);
  }, [slideIndex, slides.length]);

  // Complete — navigate to next version or back
  const handleComplete = useCallback(() => {
    if (versionsFromRedux.length > 1) {
      const currentIndex = versionsFromRedux.findIndex((v) => v.id === versionId);
      const nextVersion = versionsFromRedux[currentIndex + 1];

      if (nextVersion) {
        dispatch(trainingRunActions.removeVersion(versionId!));

        const query: Record<string, string | boolean> = {};
        if (isGroup) query.group = 'true';
        if (supplierEmployeeId) query.supplierEmployeeId = supplierEmployeeId;
        if (isPlayground) query.playground = 'true';
        if (langParam) query.lang = langParam;
        const backTo = searchParams.get('backTo');
        if (backTo) query.backTo = backTo;

        navigate(PathKey.TrainingRun, { versionId: nextVersion.id! }, query, { replace: true });
        return;
      }
    }

    goBack('/');
  }, [versionsFromRedux, versionId, dispatch, isGroup, supplierEmployeeId, isPlayground, searchParams, navigate, goBack]);

  // Sign navigation
  const handleSignPrev = useCallback(() => {
    setMode('training');
    setSlideIndex(slides.length - 1);
  }, [slides.length]);

  const handleSignNext = useCallback(() => {
    if (questions.length === 0 || questionnaireResult) {
      handleComplete();
      return;
    }
    setMode('questionnaire');
    setQuestionIndex(0);
  }, [questions.length, questionnaireResult, handleComplete]);

  // Questionnaire
  const handleAnswerSelected = useCallback(
    (questionId: string, answerId: string) => {
      const updated = questionsRef.current.map((q) => (q.id === questionId ? { ...q, selectedAnswerId: answerId } : q));
      questionsRef.current = updated;
      setQuestions(updated);
    },
    [setQuestions],
  );

  const handleAdvance = useCallback(async () => {
    const current = questionsRef.current;
    const allAnswered = current.every((q) => q.selectedAnswerId);
    const isLastQuestion = questionIndex >= current.length - 1;

    if (!allAnswered && !isLastQuestion) {
      setQuestionIndex((prev) => prev + 1);
      return;
    }

    const answers = current.map((q) => ({ questionId: q.id!, answerId: q.selectedAnswerId! }));
    // Playground submits are graded against the version being previewed — the questionnaire version in
    // questionnaire mode, the training version (which resolves its own questionnaire) in training playground.
    const response =
      isQuestionnaireMode || isPlayground
        ? await trainingVersionService.submitPlaygroundAnswers(
            isQuestionnaireMode ? { questionnaireVersionId: versionId! } : { trainingVersionId: versionId! },
            { answers },
          )
        : await trainingVersionService.submitAnswers(versionId!, { answers });
    const updatedQuestions = current.map((q) => {
      const result = response.data.results.find((r) => r.questionId === q.id);
      return result ? { ...q, answerResponse: result.type } : q;
    });
    setQuestions(updatedQuestions);

    const correctCount = updatedQuestions.filter((q) => q.answerResponse === AnswerResponseType.Correct).length;
    const score = Math.floor((correctCount / updatedQuestions.length) * 100);
    const isOk = score >= (successRate ?? 0);

    if (isOk) {
      setQuestionnaireResult({ score, requiredScore: successRate ?? 0 });
      setMode('trainingSign');
    } else {
      setMode('overview');
    }
  }, [versionId, setQuestions, isQuestionnaireMode, isPlayground, questionIndex, successRate]);

  const handleQuestionPrev = useCallback(() => {
    setQuestionIndex((prev) => prev - 1);
  }, []);

  const handleQuestionForward = useCallback(() => {
    setQuestionIndex((prev) => prev + 1);
  }, []);

  // Overview
  const handleGoToTraining = useCallback(() => {
    setSlideIndex(0);
    setQuestions((prev) => prev.map((q) => ({ ...q, answerResponse: undefined, selectedAnswerId: undefined })));
    setMode('training');
  }, [setQuestions]);

  const handleGoToQuestionnaire = useCallback(() => {
    setQuestionIndex(0);
    setQuestions((prev) => prev.map((q) => ({ ...q, answerResponse: undefined, selectedAnswerId: undefined })));
    setMode('questionnaire');
  }, [setQuestions]);

  const renderContent = () => {
    if (mode === 'training' && slides.length > 0) {
      const canPrev = slideIndex !== 0;
      return (
        <TourniquetButtonContent
          backOnClick={canPrev ? handleSlidePrev : undefined}
          backDisabled={!canPrev}
          onAbort={handleAbort}
          nextOnClick={handleSlideNext}
          mobileGap={true}
        >
          <TourniquetSlideCard>
            <QcsBox>
              <HtmlViewer html={slides[slideIndex].content} />
            </QcsBox>
          </TourniquetSlideCard>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ShowError resource={[resources.getTrainingVersion, resources.getSlides, resources.getCurrentQuestionnaireVersion]} />
          </Box>
        </TourniquetButtonContent>
      );
    }

    if (mode === 'trainingSign') {
      return (
        <TrainingRunSign
          versionId={versionId!}
          lang={lang}
          persons={persons}
          hasQuestionnaire={questions.length > 0 && !questionnaireResult}
          afterTrainingContent={afterTrainingContent}
          isPlayground={isPlayground}
          questionnaireResult={questionnaireResult}
          questionnaireAnswers={questionnaireResult ? questions.map((q) => ({ questionId: q.id!, answerId: q.selectedAnswerId! })) : undefined}
          onPrev={slides.length > 0 ? handleSignPrev : handleAbort}
          onNext={handleSignNext}
          onAbort={handleAbort}
        />
      );
    }

    if (mode === 'questionnaire' && questions.length > 0) {
      const firstUnansweredIndex = questions.findIndex((q) => !q.selectedAnswerId);
      const maxNavigableIndex = firstUnansweredIndex === -1 ? questions.length - 1 : firstUnansweredIndex;

      return (
        <TrainingRunQuestionnaire
          questions={questions}
          questionIndex={questionIndex}
          onAnswerSelected={handleAnswerSelected}
          onAdvance={handleAdvance}
          onPrev={questionIndex > 0 ? handleQuestionPrev : undefined}
          onForward={questionIndex < maxNavigableIndex ? handleQuestionForward : undefined}
          onAbort={handleAbort}
        />
      );
    }

    if (mode === 'overview') {
      return (
        <TrainingRunOverview
          versionId={versionId!}
          lang={lang}
          questions={questions}
          successRate={successRate}
          persons={persons}
          afterTrainingContent={afterTrainingContent}
          hasSlides={slides.length > 0}
          isPlayground={isPlayground}
          onGoToTraining={handleGoToTraining}
          onGoToQuestionnaire={handleGoToQuestionnaire}
          onComplete={handleComplete}
          onAbort={handleAbort}
        />
      );
    }

    return null;
  };

  return <ShowResourceContent resource={TRAINING_RUN_RESOURCES}>{renderContent()}</ShowResourceContent>;
};
